import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Building2, Plus, RefreshCw, ExternalLink } from 'lucide-react'
import { api } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { toast } from 'sonner'
import { useCanWrite } from '@/hooks/use-permissions'

interface M365Tenant {
  id: string
  azure_tenant_id: string
  display_name: string
  domain: string | null
  status: string
  last_sync_at: string | null
  last_sync_status: string | null
  sync_enabled: number
  created_at: string
}

export function M365TenantsPage() {
  const queryClient = useQueryClient()
  const canWrite = useCanWrite()
  const [showConnect, setShowConnect] = useState(false)
  const [m365Form, setM365Form] = useState({ azureTenantId: '', displayName: '', domain: '', clientId: '', clientSecret: '' })

  const { data: tenants, isLoading } = useQuery({
    queryKey: ['m365-tenants'],
    queryFn: () => api.get<M365Tenant[]>('/m365-tenants'),
  })

  const manualConnectMutation = useMutation({
    mutationFn: () => api.post('/m365-tenants/manual', {
      azureTenantId: m365Form.azureTenantId,
      displayName: m365Form.displayName,
      domain: m365Form.domain || undefined,
      clientId: m365Form.clientId,
      clientSecret: m365Form.clientSecret,
    }),
    onSuccess: () => {
      toast.success('Tenant M365 registrado y sincronizacion iniciada')
      queryClient.invalidateQueries({ queryKey: ['m365-tenants'] })
      setShowConnect(false)
      setM365Form({ azureTenantId: '', displayName: '', domain: '', clientId: '', clientSecret: '' })
    },
    onError: () => toast.error('Error al registrar el tenant'),
  })

  const syncMutation = useMutation({
    mutationFn: (id: string) => api.post<{ jobId: string }>(`/m365-tenants/${id}/sync`),
    onSuccess: () => {
      toast.success('Sincronizacion iniciada')
      queryClient.invalidateQueries({ queryKey: ['m365-tenants'] })
    },
    onError: () => toast.error('Error al iniciar sincronizacion'),
  })

  const mf = (field: string, val: string) => setM365Form((f) => ({ ...f, [field]: val }))

  return (
    <div>
      <PageHeader
        title="Tenants Microsoft 365"
        description="Gestiona las conexiones a tus tenants de Microsoft 365"
        actions={
          canWrite ? (
            <Button onClick={() => setShowConnect(!showConnect)}>
              <Plus className="h-4 w-4" /> {showConnect ? 'Cancelar' : 'Registrar tenant'}
            </Button>
          ) : undefined
        }
      />

      {showConnect && (
        <Card>
          <p className="mb-3 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Registro manual de tenant M365
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Azure Tenant ID *
              </label>
              <input type="text" value={m365Form.azureTenantId} onChange={(e) => mf('azureTenantId', e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="w-full rounded-md border px-3 py-2 text-sm font-mono" style={{ borderColor: 'var(--color-border)' }} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Nombre del tenant *
              </label>
              <input type="text" value={m365Form.displayName} onChange={(e) => mf('displayName', e.target.value)}
                placeholder="Nombre de la organizacion"
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Dominio principal
              </label>
              <input type="text" value={m365Form.domain} onChange={(e) => mf('domain', e.target.value)}
                placeholder="empresa.onmicrosoft.com"
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
            </div>
            <div className="md:col-span-2">
              <div className="mb-2 mt-1 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
                <p className="text-xs font-medium" style={{ color: 'var(--color-text-tertiary)' }}>
                  Credenciales de la App de Azure AD
                </p>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Client ID *
              </label>
              <input type="text" value={m365Form.clientId} onChange={(e) => mf('clientId', e.target.value)}
                placeholder="Client ID de Azure AD"
                className="w-full rounded-md border px-3 py-2 text-sm font-mono" style={{ borderColor: 'var(--color-border)' }} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Client Secret *
              </label>
              <input type="password" value={m365Form.clientSecret} onChange={(e) => mf('clientSecret', e.target.value)}
                placeholder="Client Secret de Azure AD"
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={() => manualConnectMutation.mutate()}
              loading={manualConnectMutation.isPending}
              disabled={!m365Form.azureTenantId || !m365Form.displayName || !m365Form.clientId || !m365Form.clientSecret}
            >
              Registrar y sincronizar
            </Button>
            <Button variant="secondary" onClick={() => { setShowConnect(false); setM365Form({ azureTenantId: '', displayName: '', domain: '', clientId: '', clientSecret: '' }) }}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-200)' }} />
          ))}
        </div>
      ) : !tenants?.length ? (
        <Card>
          <EmptyState
            icon={Building2}
            title="Sin tenants conectados"
            description="Conecta tu primer tenant de Microsoft 365 para comenzar a analizar licencias"
            action={{ label: 'Registrar tenant', onClick: () => setShowConnect(true) }}
          />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {tenants.map((t) => (
            <Card key={t.id}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-lg"
                    style={{ backgroundColor: 'var(--color-brand-primary-50)' }}
                  >
                    <Building2 className="h-5 w-5" style={{ color: 'var(--color-brand-primary)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      {t.display_name}
                    </h3>
                    <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                      {t.domain ?? t.azure_tenant_id}
                    </p>
                  </div>
                </div>
                <StatusBadge status={t.status} />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p style={{ color: 'var(--color-text-tertiary)' }}>Ultima sync</p>
                  <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    {formatDateTime(t.last_sync_at)}
                  </p>
                </div>
                <div>
                  <p style={{ color: 'var(--color-text-tertiary)' }}>Estado sync</p>
                  {t.last_sync_status ? (
                    <StatusBadge status={t.last_sync_status} />
                  ) : (
                    <p className="font-medium" style={{ color: 'var(--color-text-tertiary)' }}>—</p>
                  )}
                </div>
              </div>

              <div className="mt-4 flex gap-2 border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
                {canWrite && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => syncMutation.mutate(t.id)}
                    loading={syncMutation.isPending}
                  >
                    <RefreshCw className="h-3.5 w-3.5" /> Sincronizar
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => (window.location.href = `/m365-tenants/${t.id}`)}
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Ver detalle
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
